/*
  Vemica bundle builder - behaviour.
  Added 2026-08-10. Shared bottom buttons added 2026-08-12.
  Draft theme 164794892530.

  HOW THE DISCOUNT IS CHOSEN
  Purely from the total selected quantity, never from which tier card is active:

      3-4 items   ->  5%  VEMICA-BUNDLE5
      5-9 items   -> 10%  VEMICA-BUNDLE10
      10-15 items -> 15%  VEMICA-BUNDLE15

  data-vbb-bands carries [{min, pct, code}, ...]; bandFor() returns the highest
  band whose minimum the quantity meets. Every price on screen and the code that
  will be sent to Shopify are recomputed from that band on every add, remove,
  quantity or variant change - so they cannot disagree with the quantity.

  WHY THE RANGE IS GLOBAL
  The cards used to own the range (Buy 3 = 3-4, Buy 5 = 5-9, Buy 10 = 10-15),
  which fought quantity-driven discounting in both directions: opening Buy 5 and
  removing to 4 disabled Add to Cart even though 4 items is a valid 5% bundle,
  and opening Buy 3 made 5 items unreachable. The range is now
  data-vbb-min-total..data-vbb-max-total (3..15) for every card, so the customer
  moves freely and the discount follows.

  Because the range no longer depends on the card, switching cards does NOT
  reset the selection any more - only the very first activation seeds the
  default product.

  WHEN THE CODE IS SENT
  A Shopify discount code can only be applied by a request, so the resolved code
  is sent once, on submit, via /discount/<CODE>. Applying a code REPLACES
  whatever code the session held, which is what moves a customer between tiers.

  ================================================================
  SHARED BOTTOM BUTTONS
  ================================================================
  The page already ends with one pair of buttons, directly below the three tier
  boxes:

      [Buy 3 box] [Buy 5 box] [Buy 10 box]
      [ Add to cart ]  [ Buy it now ]

      Add to cart  =  .product-form__submit      (Dawn's product form)
      Buy it now   =  .flexy-type-buynow         (FlexyPe app button)

  Those two are the single shared controls for whichever tier box is active. No
  new buttons were created and there are no per-row buttons. The builder's own
  [data-vbb-buy] used to live inside the picker - and because the picker is
  appended into the ACTIVE tier, that button physically sat inside the Buy 3 /
  Buy 5 / Buy 10 card, i.e. it was the duplicate. It is removed from the DOM in
  connectedCallback(); the reference is kept only so the existing enable/disable
  and loading-state bookkeeping keeps working untouched.

  HOW THE HANDOFF WORKS
  A capture-phase click listener on document intercepts the two buttons before
  the theme's product-form submit handler and before FlexyPe's own handler, then
  runs submitBundle(). Both buttons read the SAME existing state - this.rows,
  this.activeTier, selectedItems(), this.activeCode. There is no second
  selection model, and switching tier boxes changes what they submit purely
  because activeTier is what those already key off.

  Scoping: the buttons are resolved from the builder's own
  .product__info-container and compared by identity before intercepting. That
  matters because related-products quick-add modals also contain
  .product-form__submit, and a loose selector would hijack those too.

  WHY IT ONLY ENGAGES AFTER THE CUSTOMER TOUCHES THE BUILDER
  The bundle minimum is 3, but the default selection is this product x1, and a
  customer who just wants one jar must still be able to buy it. So until the
  customer engages with the builder (opens the picker, switches box, changes a
  quantity or a variant) the two buttons are left completely alone and behave
  exactly as Dawn and FlexyPe intend - including the product form's own quantity
  stepper. From the first interaction onwards they submit the bundle selection.

  KNOWN CONSEQUENCE: once the builder has been touched, the product form's
  quantity stepper no longer feeds Add to cart - the picker's per-product
  quantities do. The two controls would otherwise disagree. Flagged rather than
  hidden, because hiding the stepper would be a design change.
*/
(function () {
  'use strict';

  function formatMoney(cents) {
    var value = (Math.round(cents) / 100).toFixed(2).split('.');
    value[0] = value[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return 'Rs. ' + value.join('.');
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  var CLOSE_ICON =
    '<svg viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M3 3l12 12M15 3L3 15" stroke="currentColor" stroke-width="2"/></svg>';

  var ADD_SELECTOR = '.product-form__submit';
  var BUY_SELECTOR = '.flexy-type-buynow, [class*="flexy-type-buynow"]';

  class VemicaBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.picker = this.querySelector('[data-vbb-picker]');
      this.tiers = Array.prototype.slice.call(this.querySelectorAll('[data-vbb-tier]'));
      if (!this.picker || !this.tiers.length) return;

      /*
        Every picker lookup is scoped to the picker. The tier cards carry
        data-vbb-min / data-vbb-max / data-vbb-percent, so an unscoped
        this.querySelector('[data-vbb-max]') matches the FIRST TIER CARD, and
        writing textContent to that wiped out the entire first tier.
      */
      this.list = this.picker.querySelector('[data-vbb-list]');
      this.countEl = this.picker.querySelector('[data-vbb-count]');
      this.maxEl = this.picker.querySelector('[data-vbb-max-display]');
      this.pickerTitleEl = this.picker.querySelector('[data-vbb-picker-title]');
      this.totalEl = this.picker.querySelector('[data-vbb-total]');
      this.originalEl = this.picker.querySelector('[data-vbb-original]');
      this.saveEl = this.picker.querySelector('[data-vbb-save]');
      this.buyBtn = this.picker.querySelector('[data-vbb-buy]');
      this.noteEl = this.picker.querySelector('[data-vbb-note]');

      /*
        The duplicate. The picker is appended into the active tier, so this
        button rendered INSIDE the Buy 3 / Buy 5 / Buy 10 card. The shared pair
        at the bottom of the page replaces it. Detached rather than deleted, so
        the existing disabled / is-loading bookkeeping below needs no changes.
      */
      if (this.buyBtn && this.buyBtn.parentNode) this.buyBtn.parentNode.removeChild(this.buyBtn);

      this.defaultProductId = (this.getAttribute('data-vbb-default-product') || '').trim();
      this.defaultVariantId = parseInt(this.getAttribute('data-vbb-default-variant'), 10) || null;

      this.globalMin = parseInt(this.getAttribute('data-vbb-min-total'), 10) || 0;
      this.globalMax = parseInt(this.getAttribute('data-vbb-max-total'), 10) || 0;

      try {
        this.bands = JSON.parse(this.getAttribute('data-vbb-bands') || '[]') || [];
      } catch (e) {
        this.bands = [];
      }
      this.bands = this.bands.filter(function (b) { return b && typeof b.min === 'number'; });
      this.bands.sort(function (a, b) { return a.min - b.min; });

      var dataEl = this.querySelector('[data-vbb-products]');
      try {
        this.products = JSON.parse(dataEl.textContent) || [];
      } catch (e) {
        this.products = [];
      }
      this.products = this.products.filter(function (p) {
        return p && p.variants && p.variants.length;
      });
      if (!this.products.length) return;

      // Set the first time the customer engages with the builder - see header.
      this.userTouched = false;

      this.addEventListener('click', this.onClick.bind(this));
      this.list.addEventListener('change', this.onListChange.bind(this));
      if (this.buyBtn) this.buyBtn.addEventListener('click', this.addToCart.bind(this));

      // Capture phase: must run before Dawn's submit handler and FlexyPe's.
      document.addEventListener('click', this.onSharedButtonClick.bind(this), true);

      this.renderList();
      var initial = this.querySelector('.vbb-tier--active') || this.tiers[0];
      this.activate(initial, true);
    }

    /* ------------------------------------------- shared bottom buttons */

    // The buy area this builder belongs to. Keeps related-products quick-add
    // modals - which also contain .product-form__submit - out of scope.
    scopeEl() {
      return this.closest('.product__info-container') || this.parentElement || document.body;
    }

    sharedAddButton() {
      var s = this.scopeEl();
      return s ? s.querySelector(ADD_SELECTOR) : null;
    }

    sharedBuyButton() {
      var s = this.scopeEl();
      return s ? s.querySelector(BUY_SELECTOR) : null;
    }

    onSharedButtonClick(event) {
      if (!this.userTouched) return;              // untouched builder -> native behaviour
      if (this.busy) return;

      var el = event.target;
      if (!el || typeof el.closest !== 'function') return;
      if (this.contains(el)) return;              // our own controls, never intercept

      var hitAdd = el.closest(ADD_SELECTOR);
      var hitBuy = el.closest(BUY_SELECTOR);
      if (!hitAdd && !hitBuy) return;

      // Identity check - only THIS product's pair, not a quick-add modal's.
      if (hitAdd && hitAdd !== this.sharedAddButton()) return;
      if (hitBuy && hitBuy !== this.sharedBuyButton()) return;

      // Nothing selected: let the theme do whatever it normally would.
      if (!this.selectedItems().length) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      this.submitBundle(hitBuy ? 'checkout' : 'cart', hitBuy || hitAdd);
    }

    /* ------------------------------------------------------------ bands */

    // Highest band whose minimum the quantity meets, or null below the first.
    bandFor(quantity) {
      if (!this.bands.length) {
        return quantity >= this.min ? { min: this.min, pct: this.percent, code: this.code } : null;
      }
      var found = null;
      for (var i = 0; i < this.bands.length; i++) {
        if (quantity >= this.bands[i].min) found = this.bands[i];
      }
      return found;
    }

    // Lowest band the quantity has not reached yet, for the "add N more" hint.
    nextBand(quantity) {
      for (var i = 0; i < this.bands.length; i++) {
        if (this.bands[i].min > quantity) return this.bands[i];
      }
      return null;
    }

    /* ------------------------------------------------------------ tiers */

    activate(tier, initial) {
      if (this.activeTier === tier) return;

      if (this.activeTier) {
        this.activeTier.classList.remove('vbb-tier--active');
        this.setTierPrices(this.activeTier, '', '');
      }

      this.activeTier = tier;
      tier.classList.add('vbb-tier--active');

      var tierMin = parseInt(tier.getAttribute('data-vbb-min'), 10) || 1;
      var tierMax = parseInt(tier.getAttribute('data-vbb-max'), 10) || tierMin;

      // Global range wins when supplied; the card's own range is the fallback
      // for the Theme Editor section path, which has no bands.
      this.min = this.globalMin || tierMin;
      this.max = this.globalMax || tierMax;
      if (this.max < this.min) this.max = this.min;

      this.percent = parseFloat(tier.getAttribute('data-vbb-percent')) || 0;
      this.code = (tier.getAttribute('data-vbb-code') || '').trim();

      if (initial || !this.globalMax) this.resetSelection();

      tier.appendChild(this.picker);
      if (initial) this.picker.setAttribute('hidden', '');
      var cta = tier.querySelector('[data-vbb-cta]');
      if (cta) cta.setAttribute('aria-expanded', this.picker.hasAttribute('hidden') ? 'false' : 'true');

      this.pickerTitleEl.textContent = 'Choose ' + this.min + ' to ' + this.max + ' products';
      this.maxEl.textContent = this.max;

      this.busy = false;
      this.update();
    }

    setTierPrices(tier, total, original) {
      var t = tier.querySelector('[data-vbb-tier-total]');
      var o = tier.querySelector('[data-vbb-tier-original]');
      if (t) t.textContent = total;
      if (o) o.textContent = original;
    }

    onClick(event) {
      var cta = event.target.closest('[data-vbb-cta]');
      if (cta) {
        this.userTouched = true;
        var open = this.picker.hasAttribute('hidden');
        if (open) this.picker.removeAttribute('hidden');
        else this.picker.setAttribute('hidden', '');
        cta.setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }

      if (event.target.closest('[data-vbb-picker]')) {
        this.onPickerClick(event);
        return;
      }

      var tier = event.target.closest('[data-vbb-tier]');
      if (tier && tier !== this.activeTier) {
        this.userTouched = true;
        this.activate(tier);
      }
    }

    /* ---------------------------------------------------------- picker */

    resetSelection() {
      var self = this;
      this.rows = {};
      this.products.forEach(function (p) {
        self.rows[p.id] = { variantId: p.variants[0].id, quantity: 0 };
      });

      var seeded = this.defaultProductId && this.rows[this.defaultProductId];
      if (seeded) {
        seeded.quantity = 1;
        if (this.defaultVariantId && this.variantById(this.defaultVariantId)) {
          seeded.variantId = this.defaultVariantId;
        }
      }

      if (!this.list) return;
      Array.prototype.forEach.call(this.list.querySelectorAll('[data-vbb-row]'), function (rowEl) {
        var state = self.rows[rowEl.getAttribute('data-product-id')];
        var qty = state ? state.quantity : 0;
        rowEl.classList.toggle('is-selected', qty > 0);
        rowEl.querySelector('[data-vbb-qty]').textContent = qty;
        var select = rowEl.querySelector('[data-vbb-variant]');
        if (select && state) select.value = String(state.variantId);
      });
    }

    renderList() {
      var self = this;
      this.rows = {};
      this.list.innerHTML = this.products
        .map(function (product) {
          self.rows[product.id] = { variantId: product.variants[0].id, quantity: 0 };

          var variantControl;
          if (product.variants.length > 1) {
            variantControl =
              '<select class="vbb-product__variant vbb-product__variant--select" data-vbb-variant ' +
              'aria-label="Choose an option for ' + escapeHtml(product.title) + '">' +
              product.variants
                .map(function (v) {
                  return (
                    '<option value="' + v.id + '" data-price="' + v.price + '"' +
                    (v.available ? '' : ' disabled') + '>' +
                    escapeHtml(v.title) + ' - ' + formatMoney(v.price) + '</option>'
                  );
                })
                .join('') +
              '</select>';
          } else {
            var only = product.variants[0];
            var label = only.title && only.title !== 'Default Title' ? only.title + ' - ' : '';
            variantControl =
              '<span class="vbb-product__variant">' + escapeHtml(label) + formatMoney(only.price) + '</span>';
          }

          var media = product.image
            ? '<img src="' + escapeHtml(product.image) + '" alt="" width="40" height="40" loading="lazy">'
            : '';

          var isDefault = String(product.id) === String(self.defaultProductId);

          return (
            '<div class="vbb-product vbb-product--pick' + (isDefault ? ' vbb-product--default' : '') +
              '" data-vbb-row data-product-id="' + product.id + '">' +
              '<div class="vbb-product__media">' + media + '</div>' +
              '<div class="vbb-product__info">' +
                '<p class="vbb-product__name">' + escapeHtml(product.title) +
                  (isDefault ? ' <span class="vbb-product__flag">This product</span>' : '') +
                '</p>' +
                '<div class="vbb-product__controls">' + variantControl +
                  '<span class="vbb-qty">' +
                    '<button type="button" class="vbb-qty__btn" data-vbb-step="-1" aria-label="Decrease quantity">-</button>' +
                    '<span class="vbb-qty__value" data-vbb-qty>0</span>' +
                    '<button type="button" class="vbb-qty__btn" data-vbb-step="1" aria-label="Increase quantity">+</button>' +
                  '</span>' +
                '</div>' +
              '</div>' +
              '<button type="button" class="vbb-product__remove" data-vbb-clear aria-label="Remove ' +
                escapeHtml(product.title) + ' from the bundle">' + CLOSE_ICON + '</button>' +
            '</div>'
          );
        })
        .join('');
    }

    onPickerClick(event) {
      var stepBtn = event.target.closest('[data-vbb-step]');
      var clearBtn = event.target.closest('[data-vbb-clear]');
      if (!stepBtn && !clearBtn) return;

      var rowEl = event.target.closest('[data-vbb-row]');
      if (!rowEl) return;
      var state = this.rows[rowEl.getAttribute('data-product-id')];
      if (!state) return;

      this.userTouched = true;

      if (clearBtn) {
        state.quantity = 0;
      } else {
        var step = parseInt(stepBtn.getAttribute('data-vbb-step'), 10);
        if (step > 0 && this.totalQuantity() >= this.max) return;
        state.quantity = Math.max(0, state.quantity + step);
      }

      rowEl.querySelector('[data-vbb-qty]').textContent = state.quantity;
      rowEl.classList.toggle('is-selected', state.quantity > 0);
      this.update();
    }

    onListChange(event) {
      var select = event.target.closest('[data-vbb-variant]');
      if (!select) return;
      var rowEl = event.target.closest('[data-vbb-row]');
      var state = this.rows[rowEl.getAttribute('data-product-id')];
      if (!state) return;
      this.userTouched = true;
      state.variantId = parseInt(select.value, 10);
      this.update();
    }

    /* ----------------------------------------------------------- maths */

    variantById(id) {
      for (var i = 0; i < this.products.length; i++) {
        var vs = this.products[i].variants;
        for (var j = 0; j < vs.length; j++) if (vs[j].id === id) return vs[j];
      }
      return null;
    }

    totalQuantity() {
      var total = 0;
      for (var k in this.rows) {
        if (Object.prototype.hasOwnProperty.call(this.rows, k)) total += this.rows[k].quantity;
      }
      return total;
    }

    subtotal() {
      var sum = 0;
      for (var k in this.rows) {
        if (!Object.prototype.hasOwnProperty.call(this.rows, k)) continue;
        var state = this.rows[k];
        if (!state.quantity) continue;
        var v = this.variantById(state.variantId);
        if (v) sum += v.price * state.quantity;
      }
      return sum;
    }

    update() {
      var count = this.totalQuantity();
      var band = this.bandFor(count);
      var pct = band ? band.pct : 0;

      // The code that will be sent to Shopify, resolved from quantity alone.
      this.activeCode = band ? (band.code || '') : '';
      this.activePercent = pct;

      var gross = this.subtotal();
      var net = Math.round(gross * (1 - pct / 100));
      var qualifies = count >= this.min && count <= this.max;

      this.countEl.textContent = count;
      this.originalEl.textContent = gross > 0 && pct > 0 ? formatMoney(gross) : '';
      this.totalEl.textContent = formatMoney(net);
      if (this.saveEl) {
        this.saveEl.textContent = gross > 0 && pct > 0 ? 'You save ' + formatMoney(gross - net) : '';
      }

      this.setTierPrices(
        this.activeTier,
        gross > 0 ? formatMoney(net) : '',
        gross > 0 && pct > 0 ? formatMoney(gross) : ''
      );

      if (this.buyBtn) this.buyBtn.disabled = !qualifies;
      this.picker.classList.toggle('is-complete', qualifies);

      this.setGuidanceNote(count, pct);
    }

    setGuidanceNote(count, pct) {
      this.noteEl.classList.remove('vbb-picker__note--error');
      var first = this.bands.length ? this.bands[0] : { min: this.min, pct: this.percent };
      var next = this.nextBand(count);

      if (!pct) {
        var need = Math.max(1, first.min - count);
        this.noteEl.textContent =
          'Add ' + need + ' more ' + (need === 1 ? 'product' : 'products') +
          ' to unlock ' + first.pct + '% off.';
        return;
      }

      if (next && next.min <= this.max) {
        var more = next.min - count;
        this.noteEl.textContent =
          pct + '% off applied. Add ' + more + ' more ' +
          (more === 1 ? 'product' : 'products') + ' for ' + next.pct + '%.';
        return;
      }

      this.noteEl.textContent = pct + '% off applied. Maximum ' + this.max + ' products.';
    }

    /* -------------------------------------------------------- submitting */

    selectedItems() {
      var items = [];
      for (var k in this.rows) {
        if (!Object.prototype.hasOwnProperty.call(this.rows, k)) continue;
        var state = this.rows[k];
        if (!state.quantity) continue;
        items.push({
          id: state.variantId,
          quantity: state.quantity,
          properties: { _vemica_bundle: this.activeTier.getAttribute('data-vbb-title') || 'Bundle' },
        });
      }
      return items;
    }

    // Kept so the detached [data-vbb-buy] listener still resolves.
    addToCart() {
      this.submitBundle('cart', null);
    }

    /*
      mode: 'cart'      - add and stay, refresh the preview
            'checkout'  - add, apply the code, then go to checkout
      trigger: the shared button that was clicked, for its loading state.
    */
    submitBundle(mode, trigger) {
      var self = this;
      if (this.busy) return;
      var items = this.selectedItems();
      if (!items.length) return;

      var code = this.activeCode || '';
      var goingToCheckout = mode === 'checkout';

      this.busy = true;
      if (this.buyBtn) {
        this.buyBtn.disabled = true;
        this.buyBtn.classList.add('is-loading');
      }
      if (trigger) {
        trigger.setAttribute('aria-busy', 'true');
        trigger.classList.add('is-loading');
      }
      this.noteEl.classList.remove('vbb-picker__note--error');
      this.noteEl.textContent = goingToCheckout ? 'Preparing checkout...' : 'Adding to cart...';

      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

      var release = function () {
        self.busy = false;
        if (self.buyBtn) self.buyBtn.classList.remove('is-loading');
        if (trigger) {
          trigger.removeAttribute('aria-busy');
          trigger.classList.remove('is-loading');
        }
      };

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok) throw new Error(body.description || body.message || 'Could not add to cart.');
            return body;
          });
        })
        .then(function () {
          // Applying a code REPLACES whatever code the session held, which is
          // what moves a customer from one tier's code to another's.
          if (code) {
            return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
              headers: { Accept: 'application/json' },
            }).then(function (r) { return r.json(); });
          }
          return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          if (goingToCheckout) {
            // Leave busy set - the page is navigating away.
            self.noteEl.textContent = 'Taking you to checkout...';
            window.location.href = root + 'checkout';
            return;
          }

          release();
          self.updateCartCount(cart);

          // Reset and refresh FIRST - update() rewrites the note.
          self.resetSelection();
          self.update();

          if (code && cart && cart.total_discount > 0) {
            self.noteEl.textContent =
              'Added to cart. ' + formatMoney(cart.total_discount) + ' bundle discount applied.';
          } else if (code) {
            self.noteEl.textContent = 'Added to cart, but the bundle discount did not apply.';
            self.noteEl.classList.add('vbb-picker__note--error');
          } else {
            self.noteEl.textContent = 'Added to cart. No bundle discount applies to this quantity.';
          }
        })
        .catch(function (error) {
          release();
          if (self.buyBtn) self.buyBtn.disabled = false;
          self.noteEl.textContent = error.message || 'Something went wrong. Please try again.';
          self.noteEl.classList.add('vbb-picker__note--error');
        });
    }

    updateCartCount(cart) {
      if (!cart || typeof cart.item_count !== 'number') return;
      var bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
      Array.prototype.forEach.call(bubbles, function (el) { el.textContent = cart.item_count; });
    }
  }

  if (!customElements.get('vemica-bundle-builder')) {
    customElements.define('vemica-bundle-builder', VemicaBundleBuilder);
  }
})();
